var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createBasicCrypto, createIdentity, HASH_SHA256, SIGNING_ED25519, } from "@hyper-hyper-space/hhs3_crypto";
import { decodePublicKey, encodePublicKey } from "./identity.js";
/**
 * An ephemeral key vault suitable for browser and other in-memory runtimes.
 *
 * Identities and passphrases are retained only by this instance and are lost
 * when it is discarded.
 */
export class MemoryKeyVault {
    constructor(hashSuite = createBasicCrypto().hash(HASH_SHA256)) {
        this.hashSuite = hashSuite;
        this.keys = [];
        this.pendingLabels = new Set();
    }
    list() {
        return this.keys.map(({ record }) => copyRecord(record));
    }
    create(label_1, passphrase_1) {
        return __awaiter(this, arguments, void 0, function* (label, passphrase, signingName = SIGNING_ED25519) {
            if (this.hasLabel(label) || this.pendingLabels.has(label)) {
                throw new Error(`Key label '${label}' already exists`);
            }
            this.pendingLabels.add(label);
            try {
                const identity = yield createIdentity(signingName, this.hashSuite);
                const record = {
                    label,
                    keyId: identity.keyId,
                    publicKey: encodePublicKey(identity.publicKey),
                };
                this.keys.push({
                    record,
                    identity: copyIdentity(identity),
                    passphrase,
                });
                return copyIdentity(identity);
            }
            finally {
                this.pendingLabels.delete(label);
            }
        });
    }
    unlock(labelOrPrefix, passphrase) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.resolveKey(labelOrPrefix);
            if (key.passphrase !== passphrase) {
                throw new Error(`Wrong passphrase for key '${labelOrPrefix}'`);
            }
            return copyIdentity(key.identity);
        });
    }
    resolvePublic(labelOrPrefix) {
        const record = this.resolveKey(labelOrPrefix).record;
        return {
            keyId: record.keyId,
            publicKey: decodePublicKey(record.publicKey),
        };
    }
    resolveRecord(labelOrPrefix) {
        return copyRecord(this.resolveKey(labelOrPrefix).record);
    }
    hasLabel(label) {
        return this.keys.some((key) => key.record.label === label);
    }
    resolveKey(labelOrPrefix) {
        const normalized = labelOrPrefix.startsWith('#') ? labelOrPrefix.slice(1) : labelOrPrefix;
        const labelMatch = this.keys.find((key) => key.record.label === normalized);
        if (labelMatch !== undefined)
            return labelMatch;
        const keyMatches = this.keys.filter((key) => key.record.keyId.startsWith(normalized));
        if (keyMatches.length === 1)
            return keyMatches[0];
        if (keyMatches.length === 0)
            throw new Error(`Unknown key '${labelOrPrefix}'`);
        throw new Error(`Ambiguous key prefix '${labelOrPrefix}'`);
    }
}
function copyRecord(record) {
    return {
        label: record.label,
        keyId: record.keyId,
        publicKey: Object.assign({}, record.publicKey),
    };
}
function copyIdentity(identity) {
    return {
        keyId: identity.keyId,
        publicKey: {
            suite: identity.publicKey.suite,
            key: new Uint8Array(identity.publicKey.key),
        },
        secretKey: new Uint8Array(identity.secretKey),
    };
}
